import { useTranslation } from "react-i18next";
import { MapPin, Clock, Pencil, Trash2, ExternalLink, Heart, Compass, Check } from "lucide-react";
import { Button, Badge } from "@tabipla/ui";
import type { SpotResponseDto, TravelMode } from "@tabipla/shared";
import { getGoogleMapsUrl } from "@/utils/google-maps";
import { getCategoryIconComponent, getCategoryBadgeColors } from "@/domain/constants/categories";
import { TravelTimeIndicator } from "./itinerary/TravelTimeIndicator";

interface SpotCardProps {
  spot: SpotResponseDto;
  onEdit: (spot: SpotResponseDto) => void;
  onDelete: (spotId: string) => void;
  onSaveToFavorites?: (spot: SpotResponseDto) => void;
  onSaveToMekke?: (spot: SpotResponseDto) => void;
  isSavedToMekke?: boolean;
}

function SpotCard({
  spot,
  onEdit,
  onDelete,
  onSaveToFavorites,
  onSaveToMekke,
  isSavedToMekke,
}: SpotCardProps) {
  const CategoryIcon = getCategoryIconComponent(spot.category);
  return (
    <div className="group rounded-lg border bg-card p-3 transition-all hover:border-primary/30 hover:shadow-sm">
      <div className="flex items-start gap-3">
        {/* カテゴリアイコン */}
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${getCategoryBadgeColors(spot.category)}`}
        >
          {CategoryIcon ? <CategoryIcon className="h-5 w-5" /> : <MapPin className="h-5 w-5" />}
        </div>

        {/* コンテンツ */}
        <div className="min-w-0 flex-1">
          {/* タイトル行 */}
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h4 className="font-medium leading-snug">{spot.name}</h4>
            </div>
            {/* アクションボタン */}
            <div className="flex shrink-0 items-center gap-0.5">
              {/* 他アプリ連携ボタン - 常時表示 */}
              {onSaveToMekke &&
                (isSavedToMekke ? (
                  <div
                    className="flex h-7 w-7 items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 text-emerald-600"
                    title="Mekkeに保存済み"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7 border-indigo-200 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 hover:text-indigo-700"
                    onClick={() => onSaveToMekke(spot)}
                    title="Mekkeに保存"
                  >
                    <Compass className="h-3.5 w-3.5" />
                  </Button>
                ))}
              {/* 内部アクションボタン - モバイルでは非表示、デスクトップではホバー時のみ */}
              <div className="hidden gap-0.5 sm:flex sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100">
                {onSaveToFavorites && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-pink-500"
                    onClick={() => onSaveToFavorites(spot)}
                    title="お気に入りに追加"
                  >
                    <Heart className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  onClick={() => onEdit(spot)}
                  title="編集"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  onClick={() => onDelete(spot.id)}
                  title="削除"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>

          {/* 時間 */}
          {spot.startTime && spot.endTime && (
            <p className="mt-0.5 flex items-center text-sm text-muted-foreground">
              <Clock className="mr-1.5 h-3.5 w-3.5" />
              {spot.startTime} - {spot.endTime}
            </p>
          )}

          {/* 説明 */}
          {spot.description && (
            <p className="mt-1.5 text-sm text-muted-foreground">{spot.description}</p>
          )}

          {/* メモ（移動情報など） */}
          {spot.notes && (
            <p className="mt-1 text-xs italic text-muted-foreground/80">{spot.notes}</p>
          )}

          {/* メタ情報 */}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {spot.location && (
              <a
                href={getGoogleMapsUrl(spot)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <MapPin className="h-3 w-3 shrink-0" />
                <span className="max-w-[180px] truncate">{spot.location}</span>
                <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
              </a>
            )}
            {spot.cost != null && spot.cost > 0 && (
              <Badge variant="secondary" className="text-xs font-normal">
                {spot.cost.toLocaleString()}円
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface SpotListProps {
  spots: SpotResponseDto[];
  onEdit: (spot: SpotResponseDto) => void;
  onDelete: (spotId: string) => void;
  onSaveToFavorites?: (spot: SpotResponseDto) => void;
  onSaveToMekke?: (spot: SpotResponseDto) => void;
  onTravelModeChange?: (spotId: string, mode: TravelMode) => void;
  savedToMekkeSpotIds?: Set<string>;
}

export function SpotList({
  spots,
  onEdit,
  onDelete,
  onSaveToFavorites,
  onSaveToMekke,
  onTravelModeChange,
  savedToMekkeSpotIds,
}: SpotListProps) {
  const { t } = useTranslation();

  if (spots.length === 0) {
    return <p className="py-4 text-center text-sm text-muted-foreground">{t("spots.noSpots")}</p>;
  }

  return (
    <div className="space-y-1">
      {spots.map((spot, index) => (
        <div key={spot.id}>
          {/* Travel time indicator (skip for first spot) */}
          {index > 0 && (
            <TravelTimeIndicator
              durationMinutes={spot.travelDurationMinutes}
              distanceKm={spot.travelDistanceKm}
              mode={spot.travelMode}
              showPlaceholder={true}
              onModeChange={
                onTravelModeChange ? (mode) => onTravelModeChange(spot.id, mode) : undefined
              }
            />
          )}
          <SpotCard
            spot={spot}
            onEdit={onEdit}
            onDelete={onDelete}
            onSaveToFavorites={onSaveToFavorites}
            onSaveToMekke={onSaveToMekke}
            isSavedToMekke={savedToMekkeSpotIds?.has(spot.id)}
          />
        </div>
      ))}
    </div>
  );
}
